import { inject, Injectable } from '@angular/core';
import { bcs } from '@mysten/sui/bcs';
import { fromHex } from '@mysten/sui/utils';
import { graphql } from '@mysten/sui/graphql/schemas/latest';
import { SuiGraphQLClient } from '@mysten/sui/graphql';
import { SettingHelper } from '../../helpers/setting.helper';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class GraphqlIndexer {

    /**
     * Setting helper
     */
    setting_helper = inject(SettingHelper);

    /**
     * Graphql client
     */
    private graphql_client = new SuiGraphQLClient({ url: this.url() });

    /**
     * Read object
     * 
     * @param id
     */
    async readObject(id: string): Promise<any> {
        // Init object
        let object: any = null;

        // Prepare query
        let query = graphql(`
            query ($id: String) {
                object(address: $id) {
                    asMoveObject { contents { json }}
                }
            }
        `);

        // Execute query
        let variables = {
            id: id,
        };

        // Process results
        let result = await this.graphql_client.query({ query: query, variables: variables });
        if (result.data?.object?.asMoveObject?.contents?.json) {
            object = result.data.object.asMoveObject.contents.json;
        }

        return object;
    }

    /**
     * Read dynamic field
     * 
     * @param id
     * @param type
     * @param value
     */
    async readDynamicField(id: string, type: string, value: string): Promise<any> {
        // Init object
        let field: any = null;

        // Prepare signature
        let signature = value;
        if (type == 'address') {
            signature = bcs.Address.serialize(fromHex(value)).toBase64();
        }

        // Prepare query
        let query = graphql(`
            query ($id: String, $type: String, $signature: String) {
                owner(address: $id) {
                dynamicField(name: {type: $type, bcs: $signature}) {
                    value {
                    __typename
                    ... on MoveValue { json }
                    }
                }
                }
            }
        `);

        // Execute query
        let variables = {
            id: id,
            type: type,
            signature: signature
        };

        // Process results
        let result = await this.graphql_client.query({ query: query, variables: variables });
        if (result.data?.owner?.dynamicField?.value?.__typename == 'MoveValue') {
            field = result.data.owner.dynamicField.value.json;
        }

        return field;
    }

    /**
     * Read balance
     * 
     * @param id
     * @param signature
     */
    async readBalance(id: string, signature: string): Promise<bigint> {
        // Init balance
        let balance: bigint = BigInt(0);

        // Prepare query
        let query = graphql(`
            query ($id: String, $signature: String) {
                owner(address: $id) {
                balance(type: $signature) {
                    totalBalance
                }
                }
            }
        `);

        // Execute query
        let variables = {
            id: id,
            signature: signature
        };

        // Process results
        let result = await this.graphql_client.query({ query: query, variables: variables });
        if (result.data?.owner?.balance?.totalBalance) {
            balance = BigInt(result.data.owner.balance.totalBalance);
        }

        return balance;
    }

    /**
     * Read events
     * 
     * @param version
     * @param event_type
     * @param sender
     */
    async readEvents(version: number, event_type: string, sender?: string): Promise<Array<any>> {
        // Init events
        let events: Array<any> = [];

        // Prepare query
        let query = graphql(`
            query ($event_type: String, $page_size: Int) {
                events(filter: {eventType: $event_type}, last: $page_size) {
                    nodes {
                        timestamp
                        transactionBlock { digest }
                        contents { ... on MoveValue { json }}
                    }
                }
            }
        `);

        // Execute query
        let variables: any = {
            page_size: environment.page_size,
            event_type: environment.config.versions[version] + '::event::' + event_type
        };

        if (sender) {
            query = graphql(`
                query ($event_type: String, $sender: String, $page_size: Int) {
                    events(filter: {eventType: $event_type, sender: $sender}, last: $page_size) {
                        nodes {
                        timestamp
                        transactionBlock { digest }
                        contents { ... on MoveValue { json }}
                        }
                    }
                }
            `);

            variables.sender = sender;
        }

        // Process results
        let result = await this.graphql_client.query({ query: query, variables: variables });
        if (result.data?.events?.nodes) {
            events = result.data.events.nodes;
        }

        events.reverse();

        return events;
    }

    /**
     * Find graphql url
     */
    private url() {
        let url = environment.graphql.default;

        let provider = this.setting_helper.graphql();
        switch (provider) {
            case 'foundation':
                url = environment.graphql[provider];
                break;
        }

        return url;
    }
}